import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../axios/axiosInstance';
import { getActiveCompanyId } from 'utils/companyContext';
import { getPayrolls, previewPayroll, createPayroll, reopenPayroll, deletePayroll, updatePayroll, voidPayroll } from 'services/payrolls';
import ConfirmModal from 'components/ConfirmModal';
import { getContractors } from 'services/contractors';
import { getEmployees } from 'services/employees';
import { getPaySchedules } from 'services/paySchedules';
import Button from 'components/Button';
import Card from 'components/Card';
import PlusIcon from 'assets/images/icon/plus-white.png';
import DollarIcon from 'assets/images/icon/dollar-icon';
import RupeesIcon from 'assets/images/icon/rupees-icon';
import RupeesPocket from 'assets/images/icon/rupees-pocket';
import HandshakeIcon from 'assets/images/icon/handshake-icon';
import PayrollPreviewModal from './PayrollPreviewModal';
import ContractorPaymentsModal from './ContractorPaymentsModal';
import W2EmployeesModal from './W2EmployeesModal';
import OffCyclePayrollModal from './OffCyclePayrollModal';
import CorrectionPayrollModal from './CorrectionPayrollModal';
import useSetupChecks, { SoftSetupWarnModal } from 'components/CompanySetupChecklist';

const STATUS_LABELS = { draft: 'Draft', approved: 'Approved' };
const STATUS_COLORS = { draft: '#1B75F0', approved: '#28a745' };
const STATUS_BG    = { draft: '#E8F1FE', approved: '#d4edda' };

const SortIcon = ({ col, sortCol, sortDir }) => {
    if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4, fontSize: 11 }}>⇅</span>;
    return <span style={{ marginLeft: 4, fontSize: 11 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>;
};

const fmt      = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-';
const fmtMoney = (v)   => v != null ? `$${Number(v).toFixed(2)}` : '-';

const initialForm = { periodStart: '', periodEnd: '', payday: '', type: 'regular', applyBenefits: true, applyDeductions: true, locationId: '' };

const toDateStr = (d) => d instanceof Date ? d.toISOString().split('T')[0] : '';

function calcPeriodFromSchedule(schedule) {
    if (!schedule) return null;
    const freq = (schedule.frequency || '').toLowerCase();
    const MS = 86400000;
    const today = new Date(); today.setHours(0, 0, 0, 0);

    const anchorEnd    = schedule.firstPeriodEnd ? new Date(schedule.firstPeriodEnd) : null;
    const anchorPayday = schedule.firstPayday    ? new Date(schedule.firstPayday)    : null;
    const payOffset    = (anchorEnd && anchorPayday)
        ? Math.round((anchorPayday - anchorEnd) / MS) : 3;

    let start, end;

    if (freq === 'weekly' || freq === 'biweekly') {
        const days = freq === 'weekly' ? 7 : 14;
        let e = anchorEnd ? new Date(anchorEnd) : new Date(today);
        if (anchorEnd) {
            while (e <= today) e = new Date(e.getTime() + days * MS);
            e = new Date(e.getTime() - days * MS); // most recently completed period end
        }
        end   = e;
        start = new Date(e.getTime() - (days - 1) * MS);
    } else if (freq === 'semimonthly') {
        const m = today.getMonth(), y = today.getFullYear(), d = today.getDate();
        if (d <= 15) { start = new Date(y, m, 1);  end = new Date(y, m, 15); }
        else         { start = new Date(y, m, 16); end = new Date(y, m + 1, 0); }
    } else if (freq === 'monthly') {
        const m = today.getMonth(), y = today.getFullYear();
        start = new Date(y, m, 1); end = new Date(y, m + 1, 0);
    } else {
        return null;
    }

    return {
        periodStart: toDateStr(start),
        periodEnd:   toDateStr(end),
        payday:      toDateStr(new Date(end.getTime() + payOffset * MS)),
    };
}

const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
};

function StartNewPayroll() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [showModal, setShowModal]             = useState(false);
    const [showSetupWarn, setShowSetupWarn]     = useState(false);
    const [form, setForm]                       = useState(initialForm);
    const [errors, setErrors]                   = useState({});
    const [selectedScheduleId, setSelectedScheduleId] = useState('');
    const { allRequiredDone, missingRequired } = useSetupChecks();

    const requestOpenCreate = () => {
        if (!allRequiredDone) {
            setShowSetupWarn(true);
            return;
        }
        setShowModal(true);
    };

    const { data: paySchedulesRaw = [] } = useQuery({
        queryKey: ['pay-schedules'],
        queryFn: getPaySchedules,
    });
    const paySchedules = Array.isArray(paySchedulesRaw) ? paySchedulesRaw : [];

    // Auto-fill dates when a schedule is selected
    useEffect(() => {
        if (!selectedScheduleId) return;
        const schedule = paySchedules.find(s => String(s.id) === String(selectedScheduleId));
        const period = calcPeriodFromSchedule(schedule);
        if (period) setForm(prev => ({ ...prev, ...period }));
    }, [selectedScheduleId]); // eslint-disable-line react-hooks/exhaustive-deps
    const [confirmDelete, setConfirmDelete]     = useState(null);
    const [selectedPayroll, setSelectedPayroll] = useState(null);
    const [contractorPayroll, setContractorPayroll] = useState(null);
    const [w2EmployeePayroll, setW2EmployeePayroll] = useState(null);
    const [offCyclePayroll, setOffCyclePayroll]     = useState(null);
    const [correctionPayroll, setCorrectionPayroll] = useState(null);
    const [selectedContractorIds, setSelectedContractorIds] = useState(new Set());
    const lockedLocationId = localStorage.getItem('locationId') || '';

    const companyId = localStorage.getItem('companyId');
    const { data: locationsRaw = [] } = useQuery({
        queryKey: ['company-locations', companyId],
        queryFn: () => axiosInstance.get(`/companies/${companyId}/locations`).then(r => r.data),
        enabled: !!companyId,
        retry: false,
    });
    const locations = Array.isArray(locationsRaw) ? locationsRaw.filter(l => l.isActive !== false) : [];

    // Location-role users are locked to their location
    useEffect(() => {
        if (lockedLocationId) {
            setForm(prev => ({ ...prev, locationId: lockedLocationId }));
        }
    }, [lockedLocationId]);

    const { data: payrollsRaw, isLoading } = useQuery({
        queryKey: ['payrolls'],
        queryFn: getPayrolls,
    });

    // Fetch completed + approved timecards for the period (approve recommended but not required)
    const canFetchTimecards = showModal && form.periodStart && form.periodEnd;
    const { data: timecards = [], isLoading: loadingTimecards } = useQuery({
        queryKey: ['payroll-timecards', form.periodStart, form.periodEnd],
        queryFn: async () => {
            const cId = getActiveCompanyId();
            const res = await axiosInstance.get(`/companies/${cId}/timecards`, {
                params: {
                    status: 'completed,approved',
                    periodStart: form.periodStart,
                    periodEnd: form.periodEnd,
                },
            });
            return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        },
        enabled: !!canFetchTimecards,
        staleTime: 0,
    });

    const timecardsByEmployee = useMemo(() => {
        const map = {};
        timecards.forEach(t => {
            if (!t.employeeId) return;
            if (!map[t.employeeId]) {
                map[t.employeeId] = { employeeId: t.employeeId, workplaceId: t.workplaceId ?? null, totalHours: 0 };
            }
            map[t.employeeId].totalHours += t.paidHours ?? t.totalHours ?? 0;
            if (t.workplaceId && !map[t.employeeId].workplaceId) map[t.employeeId].workplaceId = t.workplaceId;
        });
        return Object.values(map);
    }, [timecards]);

    // Group timecards by contractor — only hourly contractors clock in/out
    const timecardsByContractor = useMemo(() => {
        const map = {};
        timecards.forEach(t => {
            if (!t.contractorId) return;
            if (!map[t.contractorId]) {
                map[t.contractorId] = { contractorId: t.contractorId, totalHours: 0 };
            }
            map[t.contractorId].totalHours += t.paidHours ?? t.totalHours ?? 0;
        });
        return Object.values(map);
    }, [timecards]);

    // Fetch all contractors — needed to auto-include all pay types in 1099_only payrolls
    const { data: allContractors = [] } = useQuery({
        queryKey: ['contractors'],
        queryFn: getContractors,
        staleTime: 5 * 60 * 1000,
    });

    // Employees — used to scope payroll to a location (salary + hourly)
    const { data: allEmployees = [] } = useQuery({
        queryKey: ['employees'],
        queryFn: getEmployees,
        staleTime: 5 * 60 * 1000,
        enabled: showModal,
    });

    const isPayrollEligibleEmployee = (e) =>
        e.active !== false &&
        e.onboardingStatus !== 'pending_review' &&
        e.onboardStatus !== 'pending_review';

    const isPayrollEligibleContractor = (c) =>
        c.active !== false &&
        c.onboardingStatus !== 'pending_review' &&
        c.onboardStatus !== 'pending_review';

    /** True if employee start/term dates intersect the selected pay period. */
    const employeeInPeriod = (e, periodStart, periodEnd) => {
        if (!periodStart || !periodEnd) return true;
        const start = e.startDate || e.StartDate;
        const term = e.terminationDate || e.TerminationDate;
        if (start && start.slice(0, 10) > periodEnd) return false;
        if (term && term.slice(0, 10) < periodStart) return false;
        return true;
    };

    const locationEmployees = useMemo(() => {
        if (!form.locationId) return [];
        return (Array.isArray(allEmployees) ? allEmployees : []).filter(e =>
            isPayrollEligibleEmployee(e) &&
            employeeInPeriod(e, form.periodStart, form.periodEnd) &&
            (e.locationId === form.locationId || e.LocationId === form.locationId)
        );
    }, [allEmployees, form.locationId, form.periodStart, form.periodEnd]);

    /** Eligible employees for company-wide Regular (or location when scoped). */
    const rosterEmployees = useMemo(() => {
        const list = Array.isArray(allEmployees) ? allEmployees : [];
        return list.filter(e =>
            isPayrollEligibleEmployee(e) &&
            employeeInPeriod(e, form.periodStart, form.periodEnd) &&
            (!form.locationId || e.locationId === form.locationId || e.LocationId === form.locationId) &&
            // Schedule members + unassigned (company-wide fallback)
            (!selectedScheduleId ||
                (!e.paymentScheduleId && !e.PaymentScheduleId) ||
                e.paymentScheduleId === selectedScheduleId ||
                e.PaymentScheduleId === selectedScheduleId)
        );
    }, [allEmployees, form.locationId, form.periodStart, form.periodEnd, selectedScheduleId]);

    const buildRegularEmployeeItems = (emps, hoursByEmployee) =>
        emps.map(emp => {
            const tc = hoursByEmployee[emp.id];
            const isHourly = (emp.payType || emp.PayType || 'salary').toLowerCase() === 'hourly';
            const total = tc ? tc.totalHours : 0;
            // Light OT: split at 40 hrs/week scaled to period length
            let regularHours = 0;
            let overtimeHours = 0;
            if (total > 0 && form.periodStart && form.periodEnd) {
                const days = Math.max(1, (new Date(form.periodEnd) - new Date(form.periodStart)) / 86400000 + 1);
                const regThreshold = Math.round((days / 7) * 40);
                regularHours = Math.min(total, regThreshold);
                overtimeHours = Math.max(0, total - regThreshold);
            } else if (total > 0) {
                regularHours = Math.min(total, 40);
                overtimeHours = Math.max(0, total - 40);
            }
            return {
                employeeId: emp.id,
                payments: [{
                    workplaceId: tc?.workplaceId || emp.primaryWorkplaceId || emp.workplaceId || null,
                    isSalaryRate: !isHourly,
                    regularHours: parseFloat(regularHours.toFixed(2)),
                    overtimeHours: overtimeHours > 0 ? parseFloat(overtimeHours.toFixed(2)) : undefined,
                }],
            };
        });

    const locationContractors = useMemo(() => {
        const list = Array.isArray(allContractors) ? allContractors : [];
        const eligible = list.filter(isPayrollEligibleContractor);
        if (!form.locationId) return eligible;
        return eligible.filter(c =>
            c.locationId === form.locationId || c.LocationId === form.locationId
        );
    }, [allContractors, form.locationId]);

    // Auto-select contractors (location-scoped when a location is chosen)
    useEffect(() => {
        if (form.type === '1099_only' && locationContractors.length > 0) {
            setSelectedContractorIds(new Set(
                locationContractors
                    .filter(isPayrollEligibleContractor)
                    .map(c => c.id)
            ));
        }
    }, [form.type, form.periodStart, form.periodEnd, form.locationId, locationContractors]);

    // For 1099_only: estimate what each contractor will be paid for the selected period
    const contractorPayEstimates = useMemo(() => {
        if (form.type !== '1099_only' || !form.periodStart || !form.periodEnd) return [];
        const periodDays = Math.max(1,
            (new Date(form.periodEnd) - new Date(form.periodStart)) / (1000 * 60 * 60 * 24) + 1
        );
        const timecardHoursMap = {};
        timecards.forEach(t => {
            if (!t.contractorId) return;
            timecardHoursMap[t.contractorId] = (timecardHoursMap[t.contractorId] || 0) + (t.paidHours ?? t.totalHours ?? 0);
        });
        return locationContractors
            .filter(c => c.active !== false)
            .map(c => {
                const payType  = c.payType || 'Hourly';
                const payRate  = c.payRate || 0;
                const hours    = timecardHoursMap[c.id] ?? 0;
                let estimated;
                switch (payType) {
                    case 'Hourly':  estimated = hours * payRate; break;
                    case 'Weekly':  estimated = payRate * (periodDays / 7); break;
                    case 'Monthly': estimated = payRate * (periodDays / 30); break;
                    case 'FlatFee': estimated = payRate; break;
                    default:        estimated = payRate;
                }
                return {
                    id: c.id,
                    name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.businessName || c.id,
                    payType,
                    payRate,
                    hours: payType === 'Hourly' ? hours : null,
                    estimated: Math.round(estimated * 100) / 100,
                    hasTimecard: payType === 'Hourly' && hours > 0,
                };
            });
    }, [form.type, form.periodStart, form.periodEnd, locationContractors, timecards]);

    // Keep refs so onSuccess always has the latest values
    const timecardsByEmployeeRef   = useRef(timecardsByEmployee);
    const timecardsByContractorRef = useRef(timecardsByContractor);
    const allContractorsRef        = useRef(allContractors);
    const locationEmployeesRef     = useRef(locationEmployees);
    const rosterEmployeesRef       = useRef(rosterEmployees);
    const locationIdRef            = useRef(form.locationId);
    useEffect(() => { timecardsByEmployeeRef.current   = timecardsByEmployee;   }, [timecardsByEmployee]);
    useEffect(() => { timecardsByContractorRef.current = timecardsByContractor; }, [timecardsByContractor]);
    useEffect(() => { allContractorsRef.current        = allContractors;        }, [allContractors]);
    useEffect(() => { locationEmployeesRef.current     = locationEmployees;     }, [locationEmployees]);
    useEffect(() => { rosterEmployeesRef.current       = rosterEmployees;       }, [rosterEmployees]);
    useEffect(() => { locationIdRef.current            = form.locationId;       }, [form.locationId]);
    const payrolls = useMemo(() => Array.isArray(payrollsRaw) ? payrollsRaw : (payrollsRaw?.data ?? []), [payrollsRaw]);

    const [sortCol, setSortCol] = useState('createdAt');
    const [sortDir, setSortDir] = useState('desc');
    const [filterStatus, setFilterStatus] = useState(null);

    const handleCardFilter = (status) => setFilterStatus(prev => prev === status ? null : status);

    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCol(col);
            setSortDir('desc');
        }
    };

    const sortedPayrolls = useMemo(() => {
        const source = filterStatus ? payrolls.filter(p => p.status === filterStatus) : payrolls;
        return [...source].sort((a, b) => {
            let aVal = a[sortCol];
            let bVal = b[sortCol];
            if (['startDate', 'payDate', 'createdAt'].includes(sortCol)) {
                aVal = aVal ? new Date(aVal).getTime() : 0;
                bVal = bVal ? new Date(bVal).getTime() : 0;
            } else if (['grossPay', 'netPay', 'totalTaxes'].includes(sortCol)) {
                aVal = Number(aVal) || 0;
                bVal = Number(bVal) || 0;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [payrolls, sortCol, sortDir, filterStatus]);

    const { mutate: doCreate, isPending: creating, isError: createError, error: createErr } = useMutation({
        mutationFn: createPayroll,
        onSuccess: async (result) => {
            const payrollId = result?.id ?? result?.Id;
            if (payrollId) {
                try {
                    const latestEmployeeItems   = timecardsByEmployeeRef.current;
                    const latestContractorItems = timecardsByContractorRef.current;
                    const contractors           = allContractorsRef.current;
                    const roster                = rosterEmployeesRef.current;
                    const scopedLocationId      = locationIdRef.current;

                    let employeeItemsForUpdate;
                    if (form.type === 'regular' || (!form.type && roster.length > 0)) {
                        const hoursMap = {};
                        latestEmployeeItems.forEach(s => { hoursMap[s.employeeId] = s; });
                        employeeItemsForUpdate = buildRegularEmployeeItems(roster, hoursMap);
                    } else if (scopedLocationId && locationEmployeesRef.current.length > 0) {
                        const hoursMap = {};
                        latestEmployeeItems.forEach(s => { hoursMap[s.employeeId] = s; });
                        employeeItemsForUpdate = buildRegularEmployeeItems(locationEmployeesRef.current, hoursMap);
                    } else {
                        employeeItemsForUpdate = latestEmployeeItems.map(s => ({
                            employeeId: s.employeeId,
                            payments: [{
                                workplaceId: s.workplaceId || null,
                                isSalaryRate: false,
                                regularHours: parseFloat(s.totalHours.toFixed(2)),
                            }],
                        }));
                    }

                    let contractorItemsForUpdate;

                    if (form.type === '1099_only') {
                        // Map timecard hours (hourly contractors only)
                        const timecardHoursMap = {};
                        latestContractorItems.forEach(ci => {
                            timecardHoursMap[ci.contractorId] = parseFloat(ci.totalHours.toFixed(2));
                        });

                        // Include only SELECTED active contractors. Backend calculates grossPay per pay type:
                        //   Hourly:  hours (from timecards) × payRate
                        //   Weekly:  payRate × (periodDays / 7)
                        //   Monthly: payRate
                        //   FlatFee: payRate
                        const contractorPool = scopedLocationId
                            ? contractors.filter(c => c.locationId === scopedLocationId || c.LocationId === scopedLocationId)
                            : contractors;
                        contractorItemsForUpdate = contractorPool
                            .filter(c => c.active !== false && selectedContractorIds.has(c.id))
                            .map(c => ({
                                contractorId: c.id,
                                hours: timecardHoursMap[c.id] ?? 0,
                            }));
                    } else {
                        // Regular payroll — contractors with completed/approved timecards
                        contractorItemsForUpdate = latestContractorItems.map(c => ({
                            contractorId: c.contractorId,
                            hours: parseFloat(c.totalHours.toFixed(2)),
                        }));
                    }

                    await updatePayroll({
                        payrollId,
                        periodStart: form.periodStart,
                        periodEnd:   form.periodEnd,
                        payday:      form.payday,
                        type:        form.type,
                        locationId:  form.locationId || null,
                        paymentScheduleId: selectedScheduleId || null,
                        applyBenefits: form.applyBenefits,
                        applyDeductions: form.applyDeductions,
                        ...(employeeItemsForUpdate.length > 0   ? { employeeItems: employeeItemsForUpdate }     : {}),
                        ...(contractorItemsForUpdate.length > 0 ? { contractorItems: contractorItemsForUpdate } : {}),
                    });
                } catch (err) {
                    console.error('[Payroll] updatePayroll error:', err?.response?.data ?? err?.message);
                }
            }
            queryClient.invalidateQueries({ queryKey: ['payrolls'] });
            if (form.type === 'off_cycle') {
                setShowModal(false);
                setOffCyclePayroll({
                    ...result,
                    startDate: form.periodStart,
                    endDate:   form.periodEnd,
                    type:      form.type,
                });
            } else if (form.type === 'correction') {
                setShowModal(false);
                setCorrectionPayroll({
                    ...result,
                    startDate: form.periodStart,
                    endDate:   form.periodEnd,
                    type:      'correction',
                });
                            } else {
                                setShowModal(false);
                                setForm(initialForm);
                                setSelectedContractorIds(new Set());
                            }
        },
    });

    const { mutate: doReopen, error: reopenError, reset: resetReopen } = useMutation({
        mutationFn: reopenPayroll,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payrolls'] }),
    });

    const { mutate: doDelete } = useMutation({
        mutationFn: deletePayroll,
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['payrolls'] }); setConfirmDelete(null); },
    });

    const [confirmVoid, setConfirmVoid] = useState(null);
    const [voidError, setVoidError] = useState('');
    const { mutate: doVoid } = useMutation({
        mutationFn: voidPayroll,
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['payrolls'] }); setConfirmVoid(null); setVoidError(''); },
        onError: (err) => setVoidError(err?.response?.data?.message || err?.message || 'Failed to void payroll'),
    });

    const approvedPayrolls = useMemo(() => payrolls.filter(p => p.status === 'approved'), [payrolls]);

    const previewQueries = useQueries({
        queries: approvedPayrolls.map(p => ({
            queryKey: ['preview', p.id],
            queryFn: () => previewPayroll(p.id),
            staleTime: 5 * 60 * 1000,
            retry: false,
        })),
    });

    const totalGrossFromPreviews = useMemo(() => {
        return previewQueries.reduce((sum, q) => {
            if (!q.data) return sum;
            const empGross = (q.data.employees ?? []).reduce((s, e) => s + (e.grossPay ?? 0), 0);
            const conGross = (q.data.contractors ?? []).reduce((s, c) => s + (c.grossPay ?? ((c.hours ?? 0) * (c.payRate ?? 0))), 0);
            return sum + empGross + conGross;
        }, 0);
    }, [previewQueries]);

    const stats = useMemo(() => ({
        total:      payrolls.length,
        draft:      payrolls.filter(p => p.status === 'draft').length,
        approved:   approvedPayrolls.length,
        totalGross: totalGrossFromPreviews,
    }), [payrolls, approvedPayrolls, totalGrossFromPreviews]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'type') {
            const isCorrection = value === 'correction' || value === '1099_only';
            setForm(prev => ({ ...prev, type: value, applyBenefits: !isCorrection, applyDeductions: !isCorrection }));
        } else {
            setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const errs = {};
        if (form.type === 'regular' || form.type === '1099_only') {
            if (!form.periodStart) errs.periodStart = 'Period start is required';
            if (!form.periodEnd)   errs.periodEnd   = 'Period end is required';
            if (form.periodStart && form.periodEnd && form.periodEnd < form.periodStart)
                errs.periodEnd = 'End date must be after start date';

            // Client-side overlap check (server also enforces)
            if (form.periodStart && form.periodEnd) {
                const overlap = (Array.isArray(payrolls) ? payrolls : []).find(p => {
                    if ((p.type || 'regular') !== form.type) return false;
                    if ((p.status || '').toLowerCase() === 'voided') return false;
                    const pStart = (p.startDate || '').slice(0, 10);
                    const pEnd = (p.endDate || '').slice(0, 10);
                    if (!pStart || !pEnd) return false;
                    if (pStart > form.periodEnd || pEnd < form.periodStart) return false;
                    const pLoc = p.locationId || p.LocationId || '';
                    const fLoc = form.locationId || '';
                    if (!pLoc || !fLoc) return true; // company-wide overlaps any
                    return pLoc === fLoc;
                });
                if (overlap) {
                    errs.periodStart = `Overlaps existing ${form.type.replace('_', '-')} payroll ${fmt(overlap.startDate)} – ${fmt(overlap.endDate)}. Use non-overlapping dates.`;
                }
            }
        }
        if (!form.payday) errs.payday = 'Pay date is required';
        return errs;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }

        const hoursByEmployee = {};
        timecardsByEmployee.forEach(s => {
            hoursByEmployee[s.employeeId] = s;
        });

        let employeeItems = [];
        if (form.type === 'regular') {
            // Hybrid v2: all eligible salary + hourly; hours from completed/approved timecards
            employeeItems = buildRegularEmployeeItems(rosterEmployees, hoursByEmployee);
        } else if (form.locationId) {
            employeeItems = buildRegularEmployeeItems(locationEmployees, hoursByEmployee);
        } else {
            employeeItems = timecardsByEmployee.map(s => ({
                employeeId: s.employeeId,
                payments: [{
                    workplaceId: s.workplaceId || null,
                    isSalaryRate: false,
                    regularHours: parseFloat(s.totalHours.toFixed(2)),
                }],
            }));
        }

        // Regular + locationId stays Regular; API filters employees/contractors by LocationId.
        doCreate({
            periodStart: form.periodStart,
            periodEnd:   form.periodEnd,
            payday:      form.payday,
            type: form.type,
            frequency: '',
            processingPeriod: '',
            applyBenefits: form.applyBenefits,
            applyDeductions: form.applyDeductions,
            forceSupplementalWithholdings: false,
            locationId: form.locationId || null,
            paymentScheduleId: selectedScheduleId || null,
            ...(employeeItems.length > 0 ? { employeeItems } : {}),
        });
    };

    const cards = [
        { title: 'Total Payrolls', count: stats.total,                      icon: <DollarIcon />,    filter: null          },
        { title: 'Draft',          count: stats.draft,                       icon: <RupeesIcon />,    filter: 'draft'       },
        { title: 'Approved',       count: stats.approved,                    icon: <RupeesPocket />,  filter: 'approved'    },
        { title: 'Total Gross Pay',count: `$${stats.totalGross.toFixed(2)}`, icon: <HandshakeIcon />, filter: null          },
    ];

    return (
        <div className="content" style={{ padding: '16px 24px 16px 16px' }}>
            <SoftSetupWarnModal
                open={showSetupWarn}
                missingRequired={missingRequired}
                onCancel={() => setShowSetupWarn(false)}
                onGoSetup={() => { setShowSetupWarn(false); navigate('/dashboard/get-ready'); }}
                onContinue={() => { setShowSetupWarn(false); setShowModal(true); }}
            />

            {/* Create New Payroll Modal */}
            {showModal && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => { setShowModal(false); setSelectedScheduleId(''); setForm(initialForm); }}>
                    <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title fw-bold">Create New Payroll</h5>
                                <button type="button" className="btn-close" onClick={() => { setShowModal(false); setSelectedScheduleId(''); setForm(initialForm); }} />
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    {/* TEMP: Pay Schedule selector hidden — restore by removing `false &&` */}
                                    {false && paySchedules.length > 0 && (
                                        <div className="mb-3">
                                            <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, display: 'block', color: '#374151' }}>
                                                Pay Schedule <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional — auto-fills dates)</span>
                                            </label>
                                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <select
                                                    value={selectedScheduleId}
                                                    onChange={e => setSelectedScheduleId(e.target.value)}
                                                    className="select-base"
                                                    style={{ minWidth: 220, fontSize: 14 }}
                                                >
                                                    <option value="">— Select a schedule —</option>
                                                    {paySchedules.map(s => (
                                                        <option key={s.id} value={s.id}>
                                                            {s.name} ({s.frequency})
                                                        </option>
                                                    ))}
                                                </select>
                                                {selectedScheduleId && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { setSelectedScheduleId(''); setForm(initialForm); }}
                                                        style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                            {selectedScheduleId && form.periodStart && (
                                                <div style={{ marginTop: 8, padding: '6px 12px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0', fontSize: 13, color: '#15803d' }}>
                                                    Period: <strong>{form.periodStart}</strong> → <strong>{form.periodEnd}</strong> &nbsp;|&nbsp; Pay Date: <strong>{form.payday}</strong>
                                                    {(() => {
                                                        const related = (Array.isArray(payrolls) ? payrolls : []).filter(p =>
                                                            (p.type || 'regular') === 'regular' &&
                                                            (p.status || '').toLowerCase() !== 'voided' &&
                                                            (!p.paymentScheduleId || String(p.paymentScheduleId) === String(selectedScheduleId))
                                                        );
                                                        if (!related.length) {
                                                            return <div style={{ marginTop: 4, color: '#166534', fontSize: 12 }}>No prior Regular for this schedule — dates come from the schedule calculator.</div>;
                                                        }
                                                        const last = [...related].sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];
                                                        const end = new Date(last.endDate);
                                                        const next = new Date(end); next.setUTCDate(next.getUTCDate() + 1);
                                                        const nextStr = next.toISOString().slice(0, 10);
                                                        return (
                                                            <div style={{ marginTop: 4, color: '#166534', fontSize: 12 }}>
                                                                Last Regular ended {fmt(last.endDate)}. Suggested next start: <strong>{nextStr}</strong>
                                                                {form.periodStart !== nextStr ? ' (you can keep or edit the dates above).' : '.'}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label style={{ fontSize: '17px', fontWeight: 500 }}>Payroll Type</label>
                                            <select
                                                className="select-base full w-100"
                                                name="type"
                                                value={form.type}
                                                onChange={handleChange}
                                            >
                                                <option value="regular">Regular</option>
                                                <option value="off_cycle">Off-Cycle</option>
                                                <option value="correction">Correction</option>
                                                <option value="1099_only">1099 Only - Contractors</option>
                                            </select>
                                            {form.type === 'off_cycle' && (
                                                <p style={{ color: '#1B75F0', fontSize: '13px', marginTop: '6px' }}>
                                                    Off-Cycle payrolls are processed outside the regular schedule. You will be guided through employee selection and payment entry after creation.
                                                </p>
                                            )}
                                            {form.type === 'correction' && (
                                                <p style={{ color: '#1B75F0', fontSize: '13px', marginTop: '6px' }}>
                                                    Correction payrolls fix errors from a previous payroll. Benefits and deductions are automatically disabled.
                                                </p>
                                            )}
                                            {form.type === 'regular' && locations.length === 0 && !lockedLocationId && (
                                                <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '6px' }}>
                                                    {rosterEmployees.length} eligible employee{rosterEmployees.length === 1 ? '' : 's'} will be included.
                                                    Hourly hours come from completed/approved timecards (Time Keeping approve is recommended but not required).
                                                </p>
                                            )}
                                        </div>
                                        {(locations.length > 0 || lockedLocationId) && (
                                            <div className="col-md-6">
                                                <label style={{ fontSize: '17px', fontWeight: 500 }}>Location Scope</label>
                                                <select
                                                    className="select-base full w-100"
                                                    name="locationId"
                                                    value={form.locationId}
                                                    onChange={handleChange}
                                                    disabled={!!lockedLocationId}
                                                >
                                                    <option value="">All locations (company-wide)</option>
                                                    {locations.map(l => (
                                                        <option key={l.id} value={l.id}>{l.name}</option>
                                                    ))}
                                                </select>
                                                <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '6px', marginBottom: 0 }}>
                                                    {lockedLocationId
                                                        ? 'Your login is limited to this location.'
                                                        : 'Choose one location for a location-only Regular (or other) payroll, or leave as All for company-wide.'}
                                                </p>
                                                {form.locationId && (
                                                    <p style={{ color: '#0f766e', fontSize: '12px', marginTop: '4px', marginBottom: 0 }}>
                                                        {locationEmployees.length} employee{locationEmployees.length === 1 ? '' : 's'} at this location will be included
                                                        {form.type === 'regular' ? ' (Regular payroll for this office only).' : '.'}
                                                    </p>
                                                )}
                                                {form.type === 'regular' && !form.locationId && showModal && (
                                                    <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '6px', marginBottom: 0 }}>
                                                        {rosterEmployees.length} eligible employee{rosterEmployees.length === 1 ? '' : 's'} will be included.
                                                        Hourly hours come from completed/approved timecards (approve on Time Keeping is recommended but not required).
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        <div className="col-md-6 d-flex align-items-end gap-4 pb-2">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="applyBenefits"
                                                    checked={form.applyBenefits}
                                                    onChange={handleChange}
                                                    id="applyBenefits"
                                                />
                                                <label className="form-check-label" htmlFor="applyBenefits" style={{ fontSize: '16px' }}>Apply Benefits</label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="applyDeductions"
                                                    checked={form.applyDeductions}
                                                    onChange={handleChange}
                                                    id="applyDeductions"
                                                />
                                                <label className="form-check-label" htmlFor="applyDeductions" style={{ fontSize: '16px' }}>Apply Deductions</label>
                                            </div>
                                        </div>
                                        {(form.type === 'regular' || form.type === '1099_only') && (
                                            <div className="col-md-4">
                                                <label style={{ fontSize: '15px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Period Start*</label>
                                                <input
                                                    type="date"
                                                    name="periodStart"
                                                    value={form.periodStart}
                                                    onChange={handleChange}
                                                    className="input-base flat w-100"
                                                    style={{ height: '50px', borderRadius: '12px', padding: '10px', border: '1px solid #ccc', background: '#f4f4f4', fontSize: '16px' }}
                                                />
                                                {errors.periodStart && <p style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>{errors.periodStart}</p>}
                                            </div>
                                        )}
                                        {(form.type === 'regular' || form.type === '1099_only') && (
                                            <div className="col-md-4">
                                                <label style={{ fontSize: '15px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Period End*</label>
                                                <input
                                                    type="date"
                                                    name="periodEnd"
                                                    value={form.periodEnd}
                                                    onChange={handleChange}
                                                    className="input-base flat w-100"
                                                    style={{ height: '50px', borderRadius: '12px', padding: '10px', border: '1px solid #ccc', background: '#f4f4f4', fontSize: '16px' }}
                                                />
                                                {errors.periodEnd && <p style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>{errors.periodEnd}</p>}
                                            </div>
                                        )}
                                        <div className={(form.type !== 'regular' && form.type !== '1099_only') ? 'col-md-6' : 'col-md-4'}>
                                            <label style={{ fontSize: '15px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Pay Date*</label>
                                            <input
                                                type="date"
                                                name="payday"
                                                value={form.payday}
                                                onChange={handleChange}
                                                min={getTomorrow()}
                                                className="input-base flat w-100"
                                                style={{ height: '50px', borderRadius: '12px', padding: '10px', border: '1px solid #ccc', background: '#f4f4f4', fontSize: '16px' }}
                                            />
                                            {errors.payday && <p style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>{errors.payday}</p>}
                                        </div>
                                    </div>
                                    {/* 1099-Only: Contractor selection + pay estimate table */}
                                    {form.type === '1099_only' && contractorPayEstimates.length > 0 && form.periodStart && form.periodEnd && (
                                        <div style={{ marginTop: 20, border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden' }}>
                                            <div style={{ background: '#f8f9fa', padding: '10px 16px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <span style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>
                                                        Select Contractors — {Math.round((new Date(form.periodEnd) - new Date(form.periodStart)) / (1000 * 60 * 60 * 24) + 1)} day period
                                                    </span>
                                                    <span style={{ fontSize: 12, color: '#888' }}>
                                                        {selectedContractorIds.size} of {contractorPayEstimates.length} selected
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <span style={{ fontSize: 12, color: '#888' }}>
                                                        Est. Total: <strong style={{ color: '#198754' }}>
                                                            ${contractorPayEstimates
                                                                .filter(c => selectedContractorIds.has(c.id))
                                                                .reduce((s, c) => s + c.estimated, 0)
                                                                .toFixed(2)}
                                                        </strong>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const allIds = contractorPayEstimates.map(c => c.id);
                                                            const allSelected = allIds.every(id => selectedContractorIds.has(id));
                                                            setSelectedContractorIds(allSelected ? new Set() : new Set(allIds));
                                                        }}
                                                        style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #ced4da', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#555' }}
                                                    >
                                                        {contractorPayEstimates.every(c => selectedContractorIds.has(c.id)) ? 'Deselect All' : 'Select All'}
                                                    </button>
                                                </div>
                                            </div>
                                            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                                                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ background: '#fff', borderBottom: '1px solid #eee', position: 'sticky', top: 0, zIndex: 1 }}>
                                                            <th style={{ padding: '7px 12px', width: 36 }}></th>
                                                            <th style={{ padding: '7px 8px', textAlign: 'left', color: '#666', fontWeight: 600 }}>Contractor</th>
                                                            <th style={{ padding: '7px 8px', textAlign: 'right', color: '#666', fontWeight: 600 }}>Rate</th>
                                                            <th style={{ padding: '7px 8px', textAlign: 'right', color: '#666', fontWeight: 600 }}>Hours</th>
                                                            <th style={{ padding: '7px 16px', textAlign: 'right', color: '#666', fontWeight: 600 }}>Est. Pay</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(() => {
                                                            const PAY_TYPE_ORDER = ['Hourly', 'Weekly', 'Monthly', 'FlatFee'];
                                                            const PAY_TYPE_LABEL = { Hourly: 'Hourly', Weekly: 'Weekly', Monthly: 'Monthly', FlatFee: 'Flat Fee' };
                                                            const PAY_TYPE_STYLE = {
                                                                Hourly:  { bg: '#e3f2fd', color: '#1565c0' },
                                                                Weekly:  { bg: '#fff3e0', color: '#e65100' },
                                                                Monthly: { bg: '#e8f5e9', color: '#2e7d32' },
                                                                FlatFee: { bg: '#fce4ec', color: '#880e4f' },
                                                            };
                                                            // Group by pay type
                                                            const groups = {};
                                                            contractorPayEstimates.forEach(c => {
                                                                const key = c.payType || 'Hourly';
                                                                if (!groups[key]) groups[key] = [];
                                                                groups[key].push(c);
                                                            });
                                                            const rows = [];
                                                            PAY_TYPE_ORDER.forEach(payType => {
                                                                const group = groups[payType];
                                                                if (!group || group.length === 0) return;
                                                                const groupIds      = group.map(c => c.id);
                                                                const allChecked    = groupIds.every(id => selectedContractorIds.has(id));
                                                                const someChecked   = groupIds.some(id => selectedContractorIds.has(id));
                                                                const groupTotal    = group.filter(c => selectedContractorIds.has(c.id)).reduce((s, c) => s + c.estimated, 0);
                                                                const style         = PAY_TYPE_STYLE[payType] || PAY_TYPE_STYLE.Hourly;
                                                                const toggleGroup   = () => setSelectedContractorIds(prev => {
                                                                    const next = new Set(prev);
                                                                    if (allChecked) groupIds.forEach(id => next.delete(id));
                                                                    else            groupIds.forEach(id => next.add(id));
                                                                    return next;
                                                                });
                                                                // Group header row
                                                                rows.push(
                                                                    <tr key={`group-${payType}`} onClick={toggleGroup} style={{ background: style.bg, cursor: 'pointer', borderBottom: '1px solid #e0e0e0' }}>
                                                                        <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={allChecked}
                                                                                ref={el => { if (el) el.indeterminate = !allChecked && someChecked; }}
                                                                                onChange={() => {}}
                                                                                style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#1B75F0' }}
                                                                            />
                                                                        </td>
                                                                        <td colSpan={3} style={{ padding: '6px 8px', fontWeight: 700, color: style.color, fontSize: 12 }}>
                                                                            {PAY_TYPE_LABEL[payType]} ({group.length} contractor{group.length !== 1 ? 's' : ''})
                                                                        </td>
                                                                        <td style={{ padding: '6px 16px', textAlign: 'right', fontWeight: 700, color: style.color, fontSize: 12 }}>
                                                                            ${groupTotal.toFixed(2)}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                                // Contractor rows in this group
                                                                group.forEach(c => {
                                                                    const isSelected = selectedContractorIds.has(c.id);
                                                                    rows.push(
                                                                        <tr
                                                                            key={c.id}
                                                                            onClick={() => setSelectedContractorIds(prev => {
                                                                                const next = new Set(prev);
                                                                                if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                                                                                return next;
                                                                            })}
                                                                            style={{ borderBottom: '1px solid #f5f5f5', background: isSelected ? '#fffbf5' : '#fafafa', cursor: 'pointer', opacity: isSelected ? 1 : 0.4, transition: 'opacity 0.15s' }}
                                                                        >
                                                                            <td style={{ padding: '5px 12px', textAlign: 'center' }}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isSelected}
                                                                                    onChange={() => {}}
                                                                                    style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#1B75F0' }}
                                                                                />
                                                                            </td>
                                                                            <td style={{ padding: '5px 8px 5px 20px', fontWeight: 500, color: isSelected ? '#222' : '#999' }}>{c.name}</td>
                                                                            <td style={{ padding: '5px 8px', textAlign: 'right', color: '#555' }}>${Number(c.payRate).toFixed(2)}</td>
                                                                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                                                                                {c.payType === 'Hourly'
                                                                                    ? (c.hours > 0
                                                                                        ? <span style={{ color: '#1B75F0', fontWeight: 600 }}>⏱ {c.hours}</span>
                                                                                        : <span style={{ color: '#ccc' }}>no timecard</span>)
                                                                                    : <span style={{ color: '#ccc' }}>—</span>}
                                                                            </td>
                                                                            <td style={{ padding: '5px 16px', textAlign: 'right', fontWeight: 600, color: isSelected && c.estimated > 0 ? '#198754' : '#bbb' }}>
                                                                                ${c.estimated.toFixed(2)}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                });
                                                            });
                                                            return rows;
                                                        })()}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {createError && (
                                        <p className="text-danger mt-2">
                                            {createErr?.response?.data?.message
                                                || (typeof createErr?.response?.data === 'string' ? createErr.response.data : null)
                                                || createErr?.message
                                                || 'Failed to create payroll.'}
                                        </p>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button
                                        type="submit"
                                        className="btn"
                                        style={{ backgroundColor: '#1B75F0', color: '#fff', border: 'none' }}
                                        disabled={creating}
                                    >
                                        {creating ? 'Creating...' : 'Create Payroll'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Delete Modal */}
            {confirmDelete && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setConfirmDelete(null)}>
                    <div className="modal-dialog" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Delete Payroll</h5>
                                <button type="button" className="btn-close" onClick={() => setConfirmDelete(null)} />
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this payroll? This action cannot be undone.</p>
                                <p className="text-muted">Period: {fmt(confirmDelete.startDate)} - {fmt(confirmDelete.endDate)}</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
                                <button type="button" className="btn btn-danger" onClick={() => doDelete(confirmDelete.id)}>Delete</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {contractorPayroll && (
                <ContractorPaymentsModal
                    payroll={contractorPayroll}
                    onClose={() => setContractorPayroll(null)}
                />
            )}

            {w2EmployeePayroll && (
                <W2EmployeesModal
                    payroll={w2EmployeePayroll}
                    onClose={() => setW2EmployeePayroll(null)}
                />
            )}

            {selectedPayroll && (
                <PayrollPreviewModal
                    payroll={selectedPayroll}
                    onClose={() => setSelectedPayroll(null)}
                    onApproved={() => { setSelectedPayroll(null); queryClient.invalidateQueries({ queryKey: ['payrolls'] }); }}
                />
            )}

            {offCyclePayroll && (
                <OffCyclePayrollModal
                    payroll={offCyclePayroll}
                    onClose={() => { setOffCyclePayroll(null); setForm(initialForm); }}
                    onApproved={() => { setOffCyclePayroll(null); setForm(initialForm); queryClient.invalidateQueries({ queryKey: ['payrolls'] }); }}
                />
            )}

            {correctionPayroll && (
                <CorrectionPayrollModal
                    payroll={correctionPayroll}
                    onClose={() => { setCorrectionPayroll(null); setForm(initialForm); }}
                    onApproved={() => { setCorrectionPayroll(null); setForm(initialForm); queryClient.invalidateQueries({ queryKey: ['payrolls'] }); }}
                />
            )}

            <div className="start-new-payroll-section">
                <div style={{ padding: '0 0 16px 0' }}>
                    <div className="row mt-2">
                        {cards.map(c => (
                            <Card
                                key={c.title}
                                title={c.title}
                                count={c.count}
                                icon={c.icon}
                                isImage={false}
                                isActive={c.filter !== null && filterStatus === c.filter}
                                onClick={c.filter !== null ? () => handleCardFilter(c.filter) : undefined}
                            />
                        ))}
                    </div>
                </div>

                <div className="container-spacing">
                    <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                        <h3 className="title mb-0">Payrolls</h3>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <Button startIcon={<img src={PlusIcon} alt="add" />} onClick={requestOpenCreate}>
                                Create New Payroll
                            </Button>
                        </div>
                    </div>

                    {isLoading ? (
                        <p className="text-center mt-4">Loading payrolls...</p>
                    ) : payrolls.length === 0 ? (
                        <div className="text-center mt-5 py-5">
                            <p className="text-muted" style={{ fontSize: '18px' }}>No payrolls yet. Create your first payroll to get started.</p>
                            <Button startIcon={<img src={PlusIcon} alt="add" />} onClick={requestOpenCreate}>Create New Payroll</Button>
                        </div>
                    ) : (
                        <div className="table-container mt-2">
                            <table className="table custom-table mb-0">
                                <thead>
                                    <tr>
                                        {[
                                            { label: 'Period',       col: 'startDate'   },
                                            { label: 'Pay Date',     col: 'payDate'     },
                                            { label: 'Type',         col: 'type'        },
                                            { label: 'Status',       col: 'status'      },
                                            { label: 'Gross Pay',    col: 'grossPay'    },
                                            { label: 'Net Pay',      col: 'netPay'      },
                                            { label: 'Total Taxes',  col: 'totalTaxes'  },
                                            { label: 'Created',      col: 'createdAt'   },
                                        ].map(({ label, col }) => (
                                            <th
                                                key={col}
                                                onClick={() => handleSort(col)}
                                                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                            >
                                                {label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                                            </th>
                                        ))}
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedPayrolls.map((p) => (
                                        <tr key={p.id} className="table-data-row">
                                            <td>{p.type === 'off_cycle' ? '-' : (fmt(p.startDate) + ' - ' + fmt(p.endDate))}</td>
                                            <td>{fmt(p.payDate)}</td>
                                            <td style={{ textTransform: 'capitalize' }}>{(p.type || 'regular').replace('_', '-')}</td>
                                            <td>
                                                <span style={{
                                                    backgroundColor: STATUS_BG[p.status] || '#eee',
                                                    color: STATUS_COLORS[p.status] || '#333',
                                                    borderRadius: '53px',
                                                    padding: '4px 12px',
                                                    fontWeight: 500,
                                                    fontSize: '15px',
                                                }}>
                                                    {STATUS_LABELS[p.status] || p.status}
                                                </span>
                                            </td>
                                            <td>{fmtMoney(p.grossPay)}</td>
                                            <td>{fmtMoney(p.netPay)}</td>
                                            <td>{fmtMoney(p.totalTaxes)}</td>
                                            <td>{fmt(p.createdAt)}</td>
                                            <td>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {p.status === 'draft' && (
                                                        <>
                                                            <button
                                                                className="btn btn-sm"
                                                                style={{ backgroundColor: '#1B75F0', color: '#fff', fontSize: '13px', border: 'none' }}
                                                                onClick={() => p.type === '1099_only' ? setW2EmployeePayroll(p) : setContractorPayroll(p)}
                                                            >
                                                                {p.type === '1099_only' ? 'Add/Update W-2 Employees' : 'Add/Update Contractors'}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm"
                                                                style={{ backgroundColor: '#5c7cfa', color: '#fff', fontSize: '13px', border: 'none' }}
                                                                onClick={() => {
                                                                    if (p.type === 'off_cycle') setOffCyclePayroll(p);
                                                                    else if (p.type === 'correction') setCorrectionPayroll(p);
                                                                    else setSelectedPayroll(p);
                                                                }}
                                                            >
                                                                Preview &amp; Approve
                                                            </button>
                                                        </>
                                                    )}
                                                    {p.status === 'approved' && (
                                                        <>
                                                            <button
                                                                className="btn btn-sm"
                                                                style={{ backgroundColor: '#5c7cfa', color: '#fff', fontSize: '13px', border: 'none' }}
                                                                onClick={() => navigate(`/dashboard/payroll/${p.id}`)}
                                                            >
                                                                View Details
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                style={{ fontSize: '13px' }}
                                                                title="Reopen to draft. Blocked if ACH already completed — void instead."
                                                                onClick={() => {
                                                                    resetReopen?.();
                                                                    doReopen(p.id, {
                                                                        onError: (err) => {
                                                                            window.alert(err?.response?.data?.message || err?.message || 'Cannot reopen this payroll.');
                                                                        },
                                                                    });
                                                                }}
                                                            >
                                                                Reopen
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-warning"
                                                                style={{ fontSize: '13px' }}
                                                                onClick={() => setConfirmVoid(p)}
                                                            >
                                                                Void
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        style={{ fontSize: '13px' }}
                                                        onClick={() => setConfirmDelete(p)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {confirmVoid && (
                <ConfirmModal
                    show={!!confirmVoid}
                    title="Void Payroll"
                    message={voidError
                        ? `Error: ${voidError}`
                        : `Void payroll for ${confirmVoid.startDate ? new Date(confirmVoid.startDate).toLocaleDateString() : 'this period'}? Pending ACH will be cancelled; completed ACH will be reversed via Dwolla. Journals are removed. This cannot be undone.`}
                    confirmText="Void Payroll"
                    onConfirm={() => { setVoidError(''); doVoid(confirmVoid.id); }}
                    onCancel={() => { setConfirmVoid(null); setVoidError(''); }}
                    danger={false}
                />
            )}
        </div>
    );
}

export default StartNewPayroll;
